import type { FC, MouseEvent } from 'react';
import { memo, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Icons from '../Icons/index.ts';
import { Loader } from '../Loader/index.tsx';
import { ClearIcon, Group, Wrapper } from './styled.ts';

export interface TagProps {
  title: string;
  infoMarker?: boolean;
  error?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isInactive?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onRemove?: () => void;
  dataTestId?: string;
}

const TagComponent: FC<TagProps> = memo(function TagComponent({
  title,
  infoMarker,
  error,
  isLoading,
  isDisabled,
  isInactive,
  onClick,
  onDoubleClick,
  onRemove,
  dataTestId,
}) {
  const { colors } = useTheme();
  const iconColor = useMemo(() => {
    if (error) {
      return isDisabled || isInactive ? colors.critical10 : colors.critical100;
    }
    return isDisabled || isInactive ? colors.grey30_1 : colors.grey100_1;
  }, [error, isDisabled, isInactive, colors.grey30_1, colors.grey100_1, colors.critical10, colors.critical100]);

  const infoMarkerColor = useMemo(() => {
    if (error) {
      return isDisabled || isInactive ? colors.critical30 : colors.critical100;
    }
    return isDisabled || isInactive ? colors.grey70_1 : colors.black_1;
  }, [error, isDisabled, isInactive, colors.grey70_1, colors.black_1, colors.critical30, colors.critical100]);

  const handleRemove = useCallbackRef((e: MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  });

  return (
    <Wrapper
      $error={error}
      $isInactive={isInactive}
      $isDisabled={isDisabled}
      onClick={!isDisabled ? onClick : undefined}
      onDoubleClick={!isDisabled ? onDoubleClick : undefined}
      data-testid={dataTestId}>
      {isLoading ? <Loader cover /> : null}
      {infoMarker && <Icons.Info size={16} color={infoMarkerColor} filled />}
      {title}
      {onRemove && <ClearIcon size={16} color={iconColor} onClick={!isDisabled ? handleRemove : undefined} $isDisabled={isDisabled} />}
    </Wrapper>
  );
});

export const Tag = Object.assign(TagComponent, {
  Group,
});
